type Tier = "root" | "rise" | "radiate";
type Props = {
  value: Tier;
  onChange: (t: Tier) => void;
  onNext: () => void;
  onBack: () => void;
};

const TIERS: Array<{
  id: Tier;
  name: string;
  italic: string;
  promise: string;
  features: string[];
  accent: string;
}> = [
  {
    id: "root",
    name: "Root",
    italic: "Foundation",
    promise: "Begin your practice with movement, breath, and community.",
    features: [
      "Unlimited yoga & guided meditation",
      "Breathwork sessions",
      "Community events & workshops",
      "LifeScribe wellness dashboard",
    ],
    accent: "from-sage to-celeste",
  },
  {
    id: "rise",
    name: "Rise",
    italic: "Expansion",
    promise: "Build strength alongside the practice — body and mind together.",
    features: [
      "Everything in Root",
      "Strength training & functional fitness",
      "Mobility & recovery sessions",
      "Wearable integration",
    ],
    accent: "from-teal to-tealMuted",
  },
  {
    id: "radiate",
    name: "Radiate",
    italic: "Full Integration",
    promise: "The complete LifeScribe pathway, personalized to you.",
    features: [
      "Everything in Rise",
      "1:1 coaching & counseling",
      "Personalized LifeScribe pathway",
      "AI wellness coach (24/7)",
      "Full studio access",
    ],
    accent: "from-tealDeep to-teal",
  },
];

export function TierSelect({ value, onChange, onNext, onBack }: Props) {
  return (
    <div>
      <div className="text-center mb-8">
        <div className="text-[10px] tracking-[0.32em] text-teal font-medium uppercase mb-3">Membership</div>
        <h2 className="font-serif text-4xl text-textPrimary font-light leading-tight">
          Choose your <span className="italic">path</span>
        </h2>
        <p className="text-textBody text-sm mt-3 max-w-lg mx-auto">
          You can change tiers anytime. Most members start with Root and grow into Rise or Radiate.
        </p>
      </div>

      <div className="space-y-4">
        {TIERS.map((t) => {
          const selected = value === t.id;
          return (
            <button
              key={t.id}
              onClick={() => onChange(t.id)}
              className={`w-full text-left bg-white rounded-2xl border-2 transition-all p-6 ${
                selected ? "border-teal shadow-md" : "border-cardBorder hover:border-teal/40"
              }`}
            >
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="text-[10px] tracking-[0.28em] uppercase font-medium text-teal">{t.italic}</div>
                  <h3 className="font-serif text-2xl text-textPrimary font-light mt-1">{t.name}</h3>
                </div>
                <div className={`w-5 h-5 rounded-full border-2 transition flex items-center justify-center ${
                  selected ? "border-teal bg-teal" : "border-cardBorder"
                }`}>
                  {selected && <div className="w-2 h-2 bg-white rounded-full"></div>}
                </div>
              </div>
              <p className="text-textBody text-sm mb-4 leading-relaxed">{t.promise}</p>
              <ul className="space-y-1.5">
                {t.features.map((f) => (
                  <li key={f} className="text-xs text-textBody flex items-start">
                    <span className="text-teal mr-2 mt-0.5">·</span>
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </button>
          );
        })}
      </div>

      <div className="mt-8 flex justify-between">
        <button onClick={onBack} className="text-textBody hover:text-teal text-xs tracking-wider uppercase">
          ← Back
        </button>
        <button
          onClick={onNext}
          className="bg-teal hover:bg-tealDeep text-white tracking-wider uppercase text-xs font-medium px-10 py-3.5 rounded-lg transition"
        >
          Continue
        </button>
      </div>
    </div>
  );
}
